import { useMemo } from 'react';
import { generatePath } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';

import type { SurveyDetail, SurveyDetailWriteRequest } from 'src/types/survey';
import { apiPaths } from 'src/services/api/apiPaths';
import HttpService from 'src/services/api/http';
import { useUserContext } from 'src/components/app/context/UserContext';

export const useSurveyDetailQuery = (surveyIdOverride?: number | null) => {
  const { currentSurvey } = useUserContext();
  const surveyId = useMemo(
    () => (surveyIdOverride ? surveyIdOverride : currentSurvey?.id),
    [surveyIdOverride, currentSurvey],
  );
  const endpoint = useMemo(
    () =>
      generatePath(apiPaths.SURVEY.SETTINGS.DETAIL, {
        id: surveyId || 0,
      }),
    [surveyId],
  );

  return useQuery<SurveyDetail>({
    queryKey: [endpoint],
    enabled: !!surveyId,
    queryFn: ({ signal }) =>
      HttpService.get(endpoint, {}, true, { signal })
        .then((data) => Promise.resolve(data.data))
        .catch(({ response }) => Promise.reject(response.status)),
  });
};

export const useSurveyUpdateQuery = () => {
  const { currentSurvey } = useUserContext();

  const endpoint = useMemo(
    () =>
      generatePath(apiPaths.SURVEY.SETTINGS.DETAIL, {
        id: currentSurvey?.id,
      }),
    [currentSurvey],
  );

  return useMutation<any, any, SurveyDetailWriteRequest>({
    mutationFn: async (data) => {
      const dataWithoutPlaceholders: SurveyDetailWriteRequest = {
        ...data,
        specificSettings: {
          ...data.specificSettings,
          ...(data.specificSettings.followUp
            ? {
                followUp: {
                  ...data.specificSettings.followUp,
                  rootCauses: data.specificSettings.followUp?.rootCauses.map(
                    // Remove placeholder ids
                    (rootCause) => ({
                      name: rootCause.name,
                      isActive: rootCause.isActive,
                      ...(rootCause.id && rootCause.id > 0
                        ? { id: rootCause.id }
                        : {}),
                    }),
                  ),
                  customFields:
                    data.specificSettings.followUp?.customFields.map(
                      // Remove placeholder ids
                      (customField) => {
                        if (customField.id && customField.id > 0)
                          return customField;
                        const { id: _, ...rest } = customField;
                        return rest;
                      },
                    ),
                },
              }
            : {}),
        },
      };
      return HttpService.put(endpoint, dataWithoutPlaceholders, {}, true)
        .then((data) => Promise.resolve(data.data))
        .catch(({ response }) => Promise.reject(response.status));
    },
    onError: (error) => error,
  });
};
